<script setup>
import {onMounted, onBeforeUnmount, ref, watch} from 'vue'
import {useTimelineFeed} from '~/composables/useTimelineFeed'
import UserTimelineItem from "~/components/timeline/UserTimelineItem.vue";

const {
  items,
  pinned,
  hasMore,
  feedLoading,
  feedLoaded,
  loadingMore,
  feedErrors,
  feedEmpty,
  pendingUpdates,
  refresh,
  loadMore
} = useTimelineFeed()

onMounted(async () => {
  await refresh()
})

// Infinite scroll: fetch the next page as the sentinel nears the viewport
const sentinel = ref(null)
let observer = null
onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    if (entries.some(entry => entry.isIntersecting)) {
      loadMore()
    }
  }, {rootMargin: '600px'})
  watch(sentinel, (el, _, onCleanup) => {
    if (el) {
      observer.observe(el)
      onCleanup(() => observer?.unobserve(el))
    }
  }, {immediate: true})
})
onBeforeUnmount(() => observer?.disconnect())

const refreshToTop = async () => {
  await refresh()
  window.scrollTo({top: 0, behavior: 'smooth'})
}
</script>

<template>
  <div class="user-timeline">
    <v-banner
        v-if="pendingUpdates"
        class="mb-3 new-updates-banner"
        density="compact"
        rounded
        icon="mdi-refresh"
    >
      <template #text>New updates are available</template>
      <template #actions>
        <v-btn color="primary" variant="text" @click="refreshToTop">Refresh</v-btn>
      </template>
    </v-banner>

    <div v-if="!feedLoaded && feedEmpty" class="feed-skeleton" aria-hidden="true">
      <div v-for="n in 4" :key="n" class="feed-skel-card">
        <div class="feed-skel-top">
          <v-skeleton-loader type="avatar"/>
          <div class="feed-skel-meta">
            <v-skeleton-loader type="text" width="45%"/>
            <v-skeleton-loader type="text" width="70%"/>
          </div>
        </div>
        <v-skeleton-loader type="image" height="120" class="feed-skel-media"/>
      </div>
    </div>
    <div v-else-if="feedLoaded && feedErrors && feedEmpty">
      <p class="feed-error">Failed to load timeline.</p>
    </div>
    <div v-else-if="feedLoaded && feedEmpty">
      <no-existing-with-action
          size="medium"
          title="Your timeline is empty"
          blurb="Join clubs and record your activities to see updates related to you here."
      ></no-existing-with-action>
    </div>
    <template v-else>
      <v-progress-linear
          v-if="feedLoading"
          :indeterminate="true"
          :active="feedLoading"
          color="primary"
      />
      <div v-if="pinned.length" class="pinned-items">
        <user-timeline-item v-for="item in pinned" :item="item" :key="item.id" :pinned="true"/>
      </div>
      <!-- Simple card stack (mockup) — cursor pagination via the sentinel below. -->
      <user-timeline-item v-for="item in items" :item="item" :key="item.id"/>
      <div ref="sentinel" class="load-more-sentinel">
        <template v-if="loadingMore">
          <!-- mockup: a feed-item skeleton card + "Loading more…" line, never a spinner -->
          <div class="feed-skel">
            <v-skeleton-loader type="text" width="55%" class="feed-skel-line"/>
            <v-skeleton-loader type="image" height="64" class="feed-skel-box"/>
          </div>
          <div class="feed-more">Loading more…</div>
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
/* first-load skeleton — mirrors the stacked feed-item card layout */
.feed-skel-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
}

.feed-skel-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.feed-skel-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.feed-skel-media {
  margin-top: 14px;
  border-radius: 10px;
  overflow: hidden;
}

/* keep the skeleton bones flush inside our own card chrome */
.feed-skel-card :deep(.v-skeleton-loader) {
  background: transparent;
}

.feed-skel-top :deep(.v-skeleton-loader__avatar) {
  margin: 0;
}

.feed-skel-meta :deep(.v-skeleton-loader__text) {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.load-more-sentinel {
  min-height: 1px;
}

/* load-more: skeleton card + terminator line (mockup .feed-item skel + .feed-more) */
.feed-skel {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 16px;
  margin-top: 14px;
}

.feed-skel-box {
  margin-top: 12px;
  border-radius: 10px;
  overflow: hidden;
}

.feed-more {
  text-align: center;
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 600;
  padding: 16px 0 4px;
}

/* brand-tinted update banner per the redesign tint vocabulary */
.new-updates-banner {
  background: rgb(var(--v-theme-brandTint)) !important;
  color: rgb(var(--v-theme-primary)) !important;
  border: none;
  border-radius: 14px;
  font-size: 12.5px;
  font-weight: 600;
}

.feed-error {
  padding: 14px 16px;
  border-radius: 10px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 12.5px;
  font-weight: 600;
}
</style>
